import React from 'react'
import {
  Course,
  Module,
  ModuleContent,
  Resource,
  ResourceStatus,
  ResourceType,
} from 'Generated/academy-graphql-types'
import { WatchQueryFetchPolicy } from '@apollo/client'
import { resources } from 'Modules/Analytics/trackActionInterface/Academy/resources'
import { analytics } from 'Modules/Analytics'
import { trainings } from 'Modules/Analytics/trackActionInterface/Academy/trainings'
import { Action, UPDATE_SHOW_ACADEMY_INTRO_SECTION } from 'TypeDefs/AppContext'

export const stringsToTitleCase = (
  strings: string[],
  sortAlphabetically = true
) => {
  try {
    const titleCaseStrings = strings.map((str) =>
      str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase())
    )

    return sortAlphabetically
      ? titleCaseStrings.sort((a, b) => a.localeCompare(b)) // 0-9, A-Z
      : titleCaseStrings
  } catch {
    return []
  }
}

export const stringToTitleCase = (str: string) => {
  try {
    return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase())
  } catch {
    return str
  }
}

export const getModuleResourceTags = (module: Module) => {
  const tagsWithDupes = module.learn
    .filter((resource) => resource?.tags)
    .map((resource) => resource.tags)
    .flat()

  return Array.from(new Set(tagsWithDupes))
}

export const addEllipsis = (str: string) => `${str.trim()}…`

type UserEvent = {
  id: number
  userId: number
  eventName: string
  eventTimeStamp: Date
  eventProperties: Record<string, string>
}

export const getUserEventTags = (userEventsData: {
  userEvent: UserEvent[]
}) => {
  if (userEventsData) {
    const { userEvent } = userEventsData

    // grab the last 7 userEvents
    // eslint-disable-next-line testing-library/await-async-events
    const latestUserEvents = userEvent.slice(-7)

    // only grab tags from the event and return in a single not nested array
    const latestUserTags = latestUserEvents.flatMap(
      (latestUserEvent: UserEvent) => {
        const { resourceTags } = latestUserEvent.eventProperties
        return resourceTags
      }
    )

    // remove undefined values if there are any
    const sanitizedUserTags = latestUserTags.filter(
      (tag: string) => tag !== undefined
    )

    // create a new unique array of the user's tags
    return [...new Set(sanitizedUserTags)]
  }
  return []
}

export const buildResourcePath = (
  resource: Resource | ModuleContent | { id: string; type: string }
) => {
  try {
    const resourcePathMap = {
      activity: 'resources/activity',
      course: 'trainings/course',
      article: 'resources/article',
      template: 'resources/template',
      learning_track: 'learning-tracks',
    }

    const resourcePath =
      resourcePathMap[
        resource.type.toLowerCase() as keyof typeof resourcePathMap
      ] || 'resources'

    return resource.type === ResourceType.Course
      ? `/academy/${resourcePath}/${(resource as Course).courseId}`
      : `/academy/${resourcePath}/${resource.id}`
  } catch {
    console.error('Error building resource path', resource)
    return ''
  }
}

export const setQueryParams = (params: Record<string, string>) => {
  if (!window) return

  const queryParams = new URLSearchParams(window.location.search)
  // Add, remove, or update query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (key) {
      if (value) queryParams.set(key, value)
      else queryParams.delete(key)
    }
  })

  if (queryParams.toString()) {
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`
    window.history.replaceState(null, '', newUrl)
  }
}

export const ACADEMY_CONTEXT = { context: { clientName: 'academy' } }
export const SEARCH_CONTEXT = { context: { clientName: 'resiliaSearch' } }

export const FETCH_POLICY_NO_CACHE = {
  fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
}

type FireResourceClickAnalytics = {
  resource: Resource
  sectionOrTabTitle: string
  userId: number
  nonprofitOrgId: number
  dispatch: React.Dispatch<Action>
  showAcademyIntroSection: boolean
  position?: number
}

export const fireResourceClickAnalytics = ({
  resource,
  sectionOrTabTitle,
  userId,
  nonprofitOrgId,
  dispatch,
  showAcademyIntroSection,
  position,
}: FireResourceClickAnalytics) => {
  const fireEvents = () => {
    const { id: resourceId, type, title } = resource
    const { courseId, status } = resource as Course

    // TODO: We can clean this section switch up after Disco is stable
    let clickActionType
    switch (sectionOrTabTitle) {
      case 'In Progress':
        clickActionType = resources.In_Progress_Clicked
        break
      case 'Saved Resources':
        clickActionType = resources.Saved_Resources_Clicked
        break
      case 'Recommended':
        clickActionType = resources.Recommended_Clicked
        break
      case 'Featured':
        clickActionType = resources.Featured_Clicked
        break
      case 'Popular Courses':
        clickActionType = resources.Popular_Courses_Clicked
        break
      case 'Popular Templates':
        clickActionType = resources.Popular_Templates_Clicked
        break
      default:
        break
    }

    if (clickActionType) {
      const dispatchHomePageSectionClicked =
        analytics.trackActionCreator(clickActionType)

      dispatchHomePageSectionClicked({
        resourceId,
        resourceType: type,
        resourceName: title,
      })
    }

    const dispatchResourceClicked = analytics.trackActionCreator(
      resources.Resource_Clicked
    )
    dispatchResourceClicked({
      resourceId,
      resourceType: type,
      resourceName: title,
      section: sectionOrTabTitle,
      position,
    })

    if (type === ResourceType.Course && courseId) {
      const courseClickActionType =
        // eslint-disable-next-line no-nested-ternary
        status === ResourceStatus.Completed
          ? trainings.Review_Course_Clicked
          : status === ResourceStatus.InProgress
          ? trainings.Resume_Course_Clicked
          : trainings.Start_Course_Clicked

      const dispatchCourseClicked = analytics.trackActionCreator(
        courseClickActionType
      )

      dispatchCourseClicked({
        userId,
        nonprofitOrgId,
        topicId: null,
        courseId: resourceId,
        courseTitle: title,
      })

      // if the user dismissed the intro section course, once they start a new course we want to show this section again
      if (!showAcademyIntroSection) {
        dispatch({
          type: UPDATE_SHOW_ACADEMY_INTRO_SECTION,
          showAcademyIntroSection: true,
        })
      }
    }
  }

  fireEvents()
}

export const getResourceCta = (resource: Resource) => {
  const resourceCtaMap = {
    course: 'Start Course',
    article: 'View Article',
    template: 'View Template',
    learning_track: 'Start Learning Track',
  }

  const resourceCta =
    resourceCtaMap[
      resource.type.toLowerCase() as keyof typeof resourceCtaMap
    ] || 'Start Revsource'

  return resourceCta
}
